import { todayLocalDay, localDayOf } from "../../shared/format/local-date.js";
import { createIcons, Plus, FileText, Download, Sparkles, Filter, Search, Eye, Edit3, Trash2, ShieldAlert, CheckCircle, AlertTriangle, XCircle, RefreshCw, Link as LinkIcon, Power, ArrowRight, ShieldCheck, Shield, Flame, Activity, Wrench, Calendar } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { router } from "../router/router.js";
import type { SafetyDevice, SafetyDeviceType, SafetyDeviceStatus, Equipment } from "../../equipment/equipment-domain.js";
import type { SafetyDeviceListFilters } from "../../equipment/safety-device-service.js";
import type { AreaId, EquipmentId } from "../../shared/kernel/identifiers.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { ContextLoadTracker, CONTEXT_LOAD_STYLES } from "../context/context-load.js";
import {
  areasForCascade,
  changeCascade,
  EMPTY_CASCADE,
  equipmentsForCascade,
  LatestRequestGate,
  projectsForCascade,
  type CascadeLevel,
  type CascadeSelection
} from "../context/client-cascade.js";
import {
  AssetCascadeFields,
  type AssetCascadeSources,
  type CascadeAreaOption,
  type CascadeClientOption,
  type CascadeProjectOption
} from "../components/asset-cascade-fields.js";

class AppSafetyDevicesPage extends HTMLElement {
  private safetyDevices: SafetyDevice[] = [];
  /** Equipamentos do recorte da listagem (Cliente filtrado ou todos os autorizados). */
  private equipments: Equipment[] = [];
  /** Cache por Cliente — o formulário pode escolher um Cliente diferente do filtro. */
  private readonly equipmentsByClient = new Map<string, Equipment[]>();
  private clients: CascadeClientOption[] = [];
  private projects: CascadeProjectOption[] = [];
  private unitAreas: CascadeAreaOption[] = [];
  private isLoading = true;
  private searchTerm = "";
  private typeFilter = "ALL";
  private statusFilter = "ALL";
  private testFilter = "ALL";
  /** Filtro local CLIENTE → PROJETO → UNIDADE/ÁREA → EQUIPAMENTO (não depende do cabeçalho global). */
  private filter: CascadeSelection = EMPTY_CASCADE;
  private readonly ctxLoad = new ContextLoadTracker();
  /** A consulta de A que responder depois da de B não desenha por cima de B. */
  private readonly listGate = new LatestRequestGate();
  private unsubscribe: (() => void) | null = null;

  connectedCallback() {
    void this.loadReferenceData().then(() => this.loadData());
    this.unsubscribe = contextStore.subscribe(() => {
      void this.loadData();
    });
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  /** Contexto da chamada com o Cliente LOCAL da tela; vazio = Todos os Clientes autorizados. */
  private ctxFor(clientId: string) {
    return { ...sessionRequestContext(), activeClientId: clientId || undefined };
  }

  /** Ações sobre um dispositivo existente usam o Cliente DELE — não o filtro nem a sessão. */
  private ctxForDevice(id: string) {
    const dev = this.safetyDevices.find(d => d.id === id);
    return this.ctxFor(dev ? String(dev.clientId) : this.filter.clientId);
  }

  private get authorizedClientIds(): ReadonlySet<string> {
    return new Set(this.clients.map(c => c.id));
  }

  private errorText(err: unknown): string {
    if (err && typeof err === "object" && "safeMessage" in err && typeof err.safeMessage === "string" && err.safeMessage) return err.safeMessage;
    return err instanceof Error ? err.message : String(err);
  }

  private clientNameOf(clientId: string): string {
    return this.clients.find(c => c.id === String(clientId))?.name ?? String(clientId);
  }

  /** Clientes autorizados, Projetos e Unidades/Áreas — dados de referência da cascata. */
  private async loadReferenceData() {
    const p = window.app.platform;
    const ctx = this.ctxFor("");
    const clients = await this.ctxLoad.load("Clientes", () => p.adminService.listAuthorizedClients(ctx));
    this.clients = clients.map(c => ({ id: String(c.id), name: c.name || c.legalName || String(c.id) }));
    const projects = await this.ctxLoad.load("Projetos", () => p.projectService.listProjects(undefined, undefined, ctx));
    this.projects = projects.map(pr => ({ id: String(pr.id), code: pr.code || "", name: pr.name || "", clientId: String(pr.clientId || "") }));
    const areas = await this.ctxLoad.load("Unidades / Áreas", () => p.projectService.listUnitAreas({}, undefined, ctx));
    this.unitAreas = areas.map(a => ({ id: String(a.id), code: a.code || "", name: a.name || "", clientId: String(a.clientId || ""), projectId: String(a.projectId || "") }));
  }

  /** Equipamentos de um Cliente, com cache — usado pela cascata do formulário. */
  private async equipmentsOf(clientId: string): Promise<Equipment[]> {
    const cached = this.equipmentsByClient.get(clientId);
    if (cached) return cached;
    const list = await this.ctxLoad.load("Equipamentos", () => window.app.platform.equipmentService.listEquipments(this.ctxFor(clientId)));
    this.equipmentsByClient.set(clientId, list);
    return list;
  }

  private cascadeSources(): AssetCascadeSources {
    return {
      clients: this.clients,
      projects: this.projects,
      areas: this.unitAreas,
      authorizedClientIds: this.authorizedClientIds,
      equipmentsOf: clientId => this.equipmentsOf(clientId)
    };
  }

  private async loadData() {
    const ticket = this.listGate.begin();
    this.isLoading = true;
    this.render();

    try {
      // O recorte (Projeto, Unidade/Área, Equipamento) vai ao SERVIÇO, que
      // consulta só o que pertence a ele; o Cliente vai no contexto.
      const ctx = this.ctxFor(this.filter.clientId);
      const filters: SafetyDeviceListFilters = {};
      if (this.filter.projectId) filters.projectId = this.filter.projectId;
      if (this.filter.areaId) filters.areaId = this.filter.areaId;
      if (this.filter.equipmentId) filters.protectedEquipmentId = this.filter.equipmentId;

      const [devices, eqs] = await Promise.all([
        window.app.platform.safetyDeviceService.listSafetyDevices(ctx, filters),
        this.ctxLoad.load("Equipamentos", () => window.app.platform.equipmentService.listEquipments(ctx))
      ]);
      if (!this.listGate.isCurrent(ticket)) return;

      this.safetyDevices = devices;
      this.equipments = eqs;
      for (const clientId of new Set(eqs.map(e => String(e.clientId)))) {
        this.equipmentsByClient.set(clientId, eqs.filter(e => String(e.clientId) === clientId));
      }
      this.isLoading = false;
      this.render();
    } catch (e) {
      if (!this.listGate.isCurrent(ticket)) return;
      console.error("[PSV] Falha ao carregar a listagem", e);
      this.isLoading = false;
      this.renderError(this.errorText(e));
    }
  }

  /** CLIENTE → PROJETO → UNIDADE/ÁREA → EQUIPAMENTO da listagem; cada nível só mostra o compatível com os de cima. */
  private renderCascadeFilters(): string {
    const sel = this.filter;
    const authorized = this.authorizedClientIds;
    const projects = projectsForCascade(this.projects, sel, authorized);
    const areas = areasForCascade(this.unitAreas, sel, authorized);
    const equipments = equipmentsForCascade(this.equipments, sel);
    return `
      <select class="sd-select" data-filter="clientId" aria-label="Cliente" style="min-width: 200px;">
        <option value="">${this.esc(this.ctxLoad.emptyLabel("Clientes", "Nenhum Cliente autorizado", "Todos os Clientes"))}</option>
        ${this.clients.map(c => `<option value="${this.esc(c.id)}" ${sel.clientId === c.id ? 'selected' : ''}>${this.esc(c.name)}</option>`).join('')}
      </select>
      <select class="sd-select" data-filter="projectId" aria-label="Projeto" style="min-width: 200px;">
        <option value="">Todos os Projetos</option>
        ${projects.map(p => `<option value="${this.esc(p.id)}" ${sel.projectId === p.id ? 'selected' : ''}>${this.esc(p.code)} — ${this.esc(p.name)}${sel.clientId ? '' : ` (${this.esc(this.clientNameOf(p.clientId))})`}</option>`).join('')}
      </select>
      <select class="sd-select" data-filter="areaId" aria-label="Unidade / Área" style="min-width: 180px;">
        <option value="">Todas as Unidades / Áreas</option>
        ${areas.map(a => `<option value="${this.esc(a.id)}" ${sel.areaId === a.id ? 'selected' : ''}>${this.esc([a.code, a.name].filter(Boolean).join(' - '))}</option>`).join('')}
      </select>
      <select class="sd-select" data-filter="equipmentId" aria-label="Equipamento protegido" style="min-width: 180px;">
        <option value="">Todos os Equipamentos</option>
        ${equipments.map(e => `<option value="${this.esc(e.id)}" ${sel.equipmentId === e.id ? 'selected' : ''}>${this.esc(e.tag)} - ${this.esc(e.name)}</option>`).join('')}
      </select>`;
  }

  private getFilteredDevices(): SafetyDevice[] {
    return this.safetyDevices.filter(dev => {
      const matchesSearch = !this.searchTerm || 
        dev.tag.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        dev.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (dev.service && dev.service.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
        (dev.manufacturer && dev.manufacturer.toLowerCase().includes(this.searchTerm.toLowerCase()));

      const matchesType = this.typeFilter === 'ALL' || dev.deviceType === this.typeFilter;
      const matchesStatus = this.statusFilter === 'ALL' || dev.status === this.statusFilter;

      let matchesTest = true;
      if (this.testFilter === 'VALID') {
        matchesTest = !!dev.nextTestDate && new Date(dev.nextTestDate) >= new Date();
      } else if (this.testFilter === 'DUE') {
        matchesTest = !dev.nextTestDate || new Date(dev.nextTestDate) < new Date() || dev.status === 'TEST_DUE';
      }

      return matchesSearch && matchesType && matchesStatus && matchesTest;
    });
  }

  private getStatusBadge(status: SafetyDeviceStatus): string {
    switch (status) {
      case 'ACTIVE':
        return `<span style="background: rgba(34, 197, 94, 0.15); color: var(--status-success-text); border: 1px solid rgba(34, 197, 94, 0.3); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #4ade80;"></span> ATIVO</span>`;
      case 'INACTIVE':
        return `<span style="background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: var(--status-danger-solid);"></span> INATIVO</span>`;
      case 'OUT_OF_SERVICE':
        return `<span style="background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: var(--status-warning-text);"></span> FORA DE OPERAÇÃO</span>`;
      case 'TEST_DUE':
        return `<span style="background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: var(--status-warning-text);"></span> TESTE VENCIDO</span>`;
      default:
        return `<span style="background: rgba(140, 140, 140, 0.15); color: var(--ia-fg-soft, #b5b5b5); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem;">${status}</span>`;
    }
  }

  private getTypeLabel(type: SafetyDeviceType): string {
    const map: Record<string, string> = {
      'PSV': 'Válvula de Segurança e Alívio (PSV)',
      'PRV': 'Válvula Reguladora de Pressão (PRV)',
      'RELIEF_VALVE': 'Válvula de Alívio',
      'SAFETY_VALVE': 'Válvula de Segurança',
      'RUPTURE_DISC': 'Disco de Ruptura (RD)',
      'VACUUM_RELIEF_VALVE': 'Válvula Quebra-Vácuo',
      'PRESSURE_VACUUM_VALVE': 'Válvula Pressão/Vácuo (PVRV)',
      'BREATHER_VALVE': 'Válvula de Respiro',
      'OTHER': 'Outro Dispositivo'
    };
    return map[type] || type;
  }

  private esc(value: string): string {
return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
private renderError(msg: string) {
    this.innerHTML = `
      <div style="padding: 32px; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; margin: 24px; color: var(--status-danger-solid);">
        <h3 style="margin: 0 0 8px 0; font-size: 1.1rem;">Erro ao carregar Dispositivos de Segurança</h3>
        <p style="margin: 0 0 16px 0; color: var(--color-text-disabled);">${msg}</p>
        <button id="btn-retry" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--ia-line, #52525b); padding: 8px 16px; border-radius: 4px; cursor: pointer;">
          Tentar Novamente
        </button>
      </div>
    `;
    this.querySelector('#btn-retry')?.addEventListener('click', () => this.loadData());
  }

  private render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div style="padding: 72px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: var(--color-text-muted);">
          <svg class="spinner spinner-dark spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Carregando...">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <span class="text-body-small">Carregando dispositivos de segurança...</span>
        </div>
      `;
      return;
    }

    const filtered = this.getFilteredDevices();
    const canCreate = contextStore.hasPermission(PermissionCode.SafetyDeviceCreate);
    const canDelete = contextStore.hasPermission(PermissionCode.SafetyDeviceDelete);
    const canUpdate = contextStore.hasPermission(PermissionCode.SafetyDeviceUpdate);
    // Com "Todos os Clientes" a mesma TAG pode existir em Clientes diferentes:
    // a coluna Cliente distingue.
    const showClientColumn = !this.filter.clientId;

    this.innerHTML = `
      <style>
        .sd-page-container {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
          color: var(--color-text-primary);
        }
        .sd-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 16px;
        }
        .sd-title-group h1 {
          font-size: 1.4rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .sd-subtitle {
          font-size: 0.85rem;
          color: var(--color-text-primary);
        }
        .sd-toolbar {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
          flex-wrap: wrap;
          align-items: center;
          background: var(--ia-bg-panel, #18181b);
          padding: 14px;
          border-radius: 8px;
          border: 1px solid var(--ia-line, #27272a);
        }
        .sd-search-box {
          flex: 1;
          min-width: 220px;
          position: relative;
        }
        .sd-search-box input {
          width: 100%;
          background: var(--color-input-bg);
          border: 1px solid var(--color-input-border);
          border-radius: 6px;
          padding: 8px 12px 8px 34px;
          color: var(--color-text-primary);
          font-size: 0.875rem;
          box-sizing: border-box;
        }
        .sd-search-box i {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--color-text-primary);
          pointer-events: none;
        }
        .sd-select {
          background: var(--color-input-bg);
          border: 1px solid var(--color-input-border);
          border-radius: 6px;
          padding: 8px 12px;
          color: var(--color-text-primary);
          font-size: 0.875rem;
          min-width: 150px;
        }
        .btn-primary-sd {
          background: var(--ia-action-bg, var(--color-primary));
          color: var(--ia-action-fg, var(--color-text-primary));
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s;
        }
        .btn-primary-sd:hover {
          background: var(--color-primary);
        }
        .btn-secondary-sd {
          background: var(--ia-bg-panel, #27272a);
          color: var(--color-text-primary);
          border: 1px solid var(--color-input-border);
          padding: 8px 14px;
          border-radius: 6px;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: background 0.2s;
        }
        .btn-secondary-sd:hover {
          background: var(--ia-bg-raised, #3f3f46);
        }
        .sd-table-wrapper {
          background: var(--ia-bg-panel, #18181b);
          border: 1px solid var(--ia-line, #27272a);
          border-radius: 8px;
          overflow-x: auto;
        }
        .sd-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
          text-align: left;
        }
        .sd-table th {
          background: var(--ia-bg-panel, #27272a);
          color: var(--color-text-disabled);
          padding: 12px 16px;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid var(--ia-line, #3f3f46);
        }
        .sd-table td {
          padding: 14px 16px;
          border-bottom: 1px solid #27272a;
          color: var(--color-text-primary);
        }
        .sd-table tr:hover td {
          background: rgba(255, 255, 255, 0.02);
        }
        .sd-tag-cell {
          font-weight: 600;
          color: var(--status-danger-solid);
          font-family: monospace;
          font-size: 0.95rem;
        }
        .sd-action-btn {
          background: var(--ia-bg-panel, #27272a);
          border: 1px solid var(--color-input-border);
          color: var(--color-text-primary);
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 0.75rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: background 0.15s;
          margin-right: 4px;
        }
        .sd-action-btn:hover {
          background: var(--ia-bg-raised, #3f3f46);
          color: var(--color-text-primary);
        }
        .sd-action-btn.danger:hover {
          background: var(--status-danger-solid);
          color: var(--color-text-primary);
        }
        .sd-action-btn.warning:hover {
          background: var(--status-warning-text);
          color: var(--color-text-primary);
        }
        .sd-stat-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }
        .sd-stat-card {
          background: var(--ia-bg-panel, #18181b);
          border: 1px solid var(--ia-line, #27272a);
          border-radius: 8px;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .sd-stat-num {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }
        .sd-stat-label {
          font-size: 0.75rem;
          color: var(--color-text-primary);
          text-transform: uppercase;
        }
        ${CONTEXT_LOAD_STYLES}
      </style>

      <div class="sd-page-container">
        <!-- HEADER -->
        <div class="sd-header">
          <div class="sd-title-group">
            <h1><i data-lucide="shield-check" style="color: var(--status-danger-solid); width: 24px; height: 24px;"></i> Dispositivos de Segurança (PSV / PRV)</h1>
            <!-- [CORREÇÃO PONTUAL] Removido texto residual "Cliente Ativo". O filtro local é a única referência visual de Cliente. -->
<div class="sd-subtitle">Válvulas de Alívio, Discos de Ruptura e Testes de Pressão</div>
          </div>
          <div style="display: flex; gap: 8px;">
            <button class="btn-secondary-sd" id="btn-refresh-list">
              <i data-lucide="refresh-cw" style="width: 16px; height: 16px;"></i> Atualizar
            </button>
            <button class="btn-secondary-sd" id="btn-goto-hub">
              <i data-lucide="link" style="width: 16px; height: 16px;"></i> Hub Técnico
            </button>
            ${canCreate ? `
              <button class="btn-primary-sd" id="btn-new-sd">
                <i data-lucide="plus" style="width: 18px; height: 18px;"></i> Nova PSV / Dispositivo
              </button>
            ` : ''}
          </div>
        </div>

        <!-- STATS -->
        <div class="sd-stat-cards">
          <div class="sd-stat-card">
            <div>
              <div class="sd-stat-label">Total Dispositivos</div>
              <div class="sd-stat-num">${this.safetyDevices.length}</div>
            </div>
            <i data-lucide="shield" style="width: 28px; height: 28px; color: var(--status-danger-solid); ;"></i>
          </div>
          <div class="sd-stat-card">
            <div>
              <div class="sd-stat-label">Testes Válidos</div>
              <div class="sd-stat-num" style="color: var(--status-success-text);">
                ${this.safetyDevices.filter(d => !!d.nextTestDate && new Date(d.nextTestDate) >= new Date()).length}
              </div>
            </div>
            <i data-lucide="check-circle" style="width: 28px; height: 28px; color: var(--status-success-text); ;"></i>
          </div>
          <div class="sd-stat-card">
            <div>
              <div class="sd-stat-label">Testes Vencidos / Pendentes</div>
              <div class="sd-stat-num" style="color: var(--status-danger-solid);">
                ${this.safetyDevices.filter(d => !d.nextTestDate || new Date(d.nextTestDate) < new Date() || d.status === 'TEST_DUE').length}
              </div>
            </div>
            <i data-lucide="alert-triangle" style="width: 28px; height: 28px; color: var(--status-danger-solid); ;"></i>
          </div>
          <div class="sd-stat-card">
            <div>
              <div class="sd-stat-label">Equipamentos Protegidos</div>
              <div class="sd-stat-num" style="color: var(--ia-info-fg, #60a5fa);">
                ${this.safetyDevices.filter(d => !!d.protectedEquipmentId).length}
              </div>
            </div>
            <i data-lucide="link" style="width: 28px; height: 28px; color: var(--ia-info-fg, #60a5fa); ;"></i>
          </div>
        </div>

        <!-- TOOLBAR & FILTERS -->
        ${this.ctxLoad.banner()}
        <div class="sd-toolbar">
          ${this.renderCascadeFilters()}
          <div class="sd-search-box">
            <i data-lucide="search" style="width: 16px; height: 16px;"></i>
            <input type="text" id="sd-search-input" placeholder="Buscar por TAG da PSV, nome, serviço, fabricante..." value="${this.searchTerm}">
          </div>

          <select class="sd-select" id="sd-type-filter">
            <option value="ALL" ${this.typeFilter === 'ALL' ? 'selected' : ''}>Todos os Tipos</option>
            <option value="PSV" ${this.typeFilter === 'PSV' ? 'selected' : ''}>Válvula de Segurança (PSV)</option>
            <option value="PRV" ${this.typeFilter === 'PRV' ? 'selected' : ''}>Válvula Reguladora (PRV)</option>
            <option value="RUPTURE_DISC" ${this.typeFilter === 'RUPTURE_DISC' ? 'selected' : ''}>Disco de Ruptura (RD)</option>
            <option value="VACUUM_RELIEF_VALVE" ${this.typeFilter === 'VACUUM_RELIEF_VALVE' ? 'selected' : ''}>Quebra-Vácuo</option>
            <option value="PRESSURE_VACUUM_VALVE" ${this.typeFilter === 'PRESSURE_VACUUM_VALVE' ? 'selected' : ''}>Pressão e Vácuo (PVRV)</option>
            <option value="OTHER" ${this.typeFilter === 'OTHER' ? 'selected' : ''}>Outros</option>
          </select>

          <select class="sd-select" id="sd-test-filter">
            <option value="ALL" ${this.testFilter === 'ALL' ? 'selected' : ''}>Todos os Testes</option>
            <option value="VALID" ${this.testFilter === 'VALID' ? 'selected' : ''}>Teste em Dia</option>
            <option value="DUE" ${this.testFilter === 'DUE' ? 'selected' : ''}>Teste Vencido / Pendente</option>
          </select>

          <select class="sd-select" id="sd-status-filter">
            <option value="ALL" ${this.statusFilter === 'ALL' ? 'selected' : ''}>Todos os Status</option>
            <option value="ACTIVE" ${this.statusFilter === 'ACTIVE' ? 'selected' : ''}>Ativo</option>
            <option value="OUT_OF_SERVICE" ${this.statusFilter === 'OUT_OF_SERVICE' ? 'selected' : ''}>Fora de Serviço</option>
            <option value="INACTIVE" ${this.statusFilter === 'INACTIVE' ? 'selected' : ''}>Inativo</option>
          </select>
        </div>

        <!-- TABLE -->
        <div class="sd-table-wrapper">
          <table class="sd-table">
            <thead>
              <tr>
                <th style="width: 130px;">TAG</th>
                ${showClientColumn ? '<th>Cliente</th>' : ''}
                <th>Nome / Tipo</th>
                <th>Equipamento Protegido</th>
                <th>Pressão de Ajuste (Set)</th>
                <th>Último Teste</th>
                <th>Próximo Teste (Venc.)</th>
                <th style="width: 100px;">Status</th>
                <th style="width: 210px; text-align: right;">Ações</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? `
                <tr>
                  <td colspan="${showClientColumn ? 9 : 8}" style="padding: 48px 16px; text-align: center; color: var(--color-text-primary);">
                    <i data-lucide="shield-alert" style="width: 40px; height: 40px; margin-bottom: 12px; ;"></i>
                    <p style="margin: 0; font-size: 0.95rem;">Nenhum dispositivo de segurança cadastrado.</p>
                    <p style="margin: 4px 0 0 0; font-size: 0.8rem; color: var(--color-text-primary);">Cadastre válvulas de segurança e alívio ou ajuste os filtros.</p>
                  </td>
                </tr>
              ` : filtered.map(dev => {
                const linkedEq = this.equipments.find(e => e.id === dev.protectedEquipmentId);
                const isExpired = !dev.nextTestDate || new Date(dev.nextTestDate) < new Date();

                return `
                  <tr>
                    <td class="sd-tag-cell">${dev.tag}</td>
                    ${showClientColumn ? `<td>${this.esc(this.clientNameOf(dev.clientId))}</td>` : ''}
                    <td>
                      <div style="font-weight: 500; color: var(--color-text-primary);">${dev.name}</div>
                      <div style="font-size: 0.75rem; color: var(--color-text-primary);">${this.getTypeLabel(dev.deviceType)} ${dev.orificeDesignation ? `&bull; Orifício: ${dev.orificeDesignation}` : ''}</div>
                    </td>
                    <td>
                      ${linkedEq ? `
                        <a href="#/equipment/${linkedEq.id}" style="color: var(--ia-info-fg, #60a5fa); text-decoration: none; font-weight: 500; display: inline-flex; align-items: center; gap: 4px;">
                          <i data-lucide="boxes" style="width: 12px; height: 12px;"></i> ${linkedEq.tag}
                        </a>
                      ` : `<span style="color: var(--color-text-primary); font-size: 0.8rem;">Não vinculado</span>`}
                    </td>
                    <td>
                      <div style="font-weight: 600; color: var(--status-danger-border); font-family: monospace;">${dev.setPressure !== null ? `${dev.setPressure} ${dev.setPressureUnit || 'kgf/cm²'}` : '-'}</div>
                      ${dev.inletSize && dev.outletSize ? `<div style="font-size: 0.72rem; color: var(--color-text-primary);">${dev.inletSize} x ${dev.outletSize}</div>` : ''}
                    </td>
                    <td>
                      <div style="font-size: 0.82rem;">${dev.lastTestDate ? new Date(dev.lastTestDate).toLocaleDateString('pt-BR') : '-'}</div>
                      ${dev.certificateNumber ? `<div style="font-size: 0.7rem; color: var(--color-text-primary);">Cert: ${dev.certificateNumber}</div>` : ''}
                    </td>
                    <td>
                      ${dev.nextTestDate ? `
                        <div style="font-size: 0.82rem; font-weight: 600; color: ${isExpired ? 'var(--status-danger-solid)' : '#4ade80'};">
                          ${new Date(dev.nextTestDate).toLocaleDateString('pt-BR')}
                        </div>
                        <span style="font-size: 0.7rem; color: ${isExpired ? 'var(--status-danger-solid)' : '#a1a1aa'};">
                          ${isExpired ? 'VENCIDO' : 'VÁLIDO'}
                        </span>
                      ` : `<span style="color: var(--color-text-primary); font-size: 0.75rem;">Não calibrado</span>`}
                    </td>
                    <td>${this.getStatusBadge(dev.status)}</td>
                    <td style="text-align: right;">
                      <button class="sd-action-btn btn-view-sd" data-id="${dev.id}" title="Ver Detalhes e Testes de Bancada">
                        <i data-lucide="eye" style="width: 14px; height: 14px;"></i>
                      </button>
                      <button class="sd-action-btn btn-test-sd" data-id="${dev.id}" title="Registrar Teste de Bancada">
                        <i data-lucide="calendar" style="width: 14px; height: 14px;"></i> Testar
                      </button>
                      ${canUpdate ? `
                        <button class="sd-action-btn btn-edit-sd" data-id="${dev.id}" title="Editar Dispositivo">
                          <i data-lucide="edit-3" style="width: 14px; height: 14px;"></i>
                        </button>
                      ` : ''}
                      ${dev.status === 'ACTIVE' ? `
                        <button class="sd-action-btn warning btn-inactivate-sd" data-id="${dev.id}" data-tag="${dev.tag}" title="Inativar">
                          <i data-lucide="power" style="width: 14px; height: 14px;"></i>
                        </button>
                      ` : `
                        <button class="sd-action-btn btn-reactivate-sd" data-id="${dev.id}" data-tag="${dev.tag}" title="Reativar" style="color: var(--status-success-text);">
                          <i data-lucide="refresh-cw" style="width: 14px; height: 14px;"></i>
                        </button>
                      `}
                      ${canDelete ? `
                        <button class="sd-action-btn danger btn-delete-sd" data-id="${dev.id}" data-tag="${dev.tag}" title="Excluir Definitivamente">
                          <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i>
                        </button>
                      ` : ''}
                    </td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
      </div>

      <!-- MODAL CONTAINER -->
      <div id="sd-modal-container"></div>
    `;

    createIcons({
      icons: { Plus, FileText, Download, Sparkles, Filter, Search, Eye, Edit3, Trash2, ShieldAlert, CheckCircle, AlertTriangle, XCircle, RefreshCw, LinkIcon, Power, ArrowRight, ShieldCheck, Shield, Flame, Activity, Wrench, Calendar },
      nameAttr: "data-lucide",
      root: this
    });

    this.bindEvents();
  }

  private bindEvents() {
    // Cascata da listagem: trocar um nível limpa os de baixo e reconsulta.
    this.querySelectorAll<HTMLSelectElement>('select[data-filter]').forEach(select => {
      select.addEventListener('change', () => {
        this.filter = changeCascade(this.filter, select.dataset['filter'] as CascadeLevel, select.value);
        void this.loadData();
      });
    });

    const searchInput = this.querySelector('#sd-search-input') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.searchTerm = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector('#sd-type-filter')?.addEventListener('change', (e) => {
      this.typeFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector('#sd-test-filter')?.addEventListener('change', (e) => {
      this.testFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector('#sd-status-filter')?.addEventListener('change', (e) => {
      this.statusFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector('#btn-refresh-list')?.addEventListener('click', () => {
      this.loadData();
    });

    this.querySelector('#btn-goto-hub')?.addEventListener('click', () => {
      router.navigate('/technical-assets');
    });

    this.querySelector('#btn-new-sd')?.addEventListener('click', () => {
      this.openCreateModal();
    });

    this.querySelectorAll('.btn-view-sd').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) router.navigate(`/safety-devices/${id}`);
      });
    });

    this.querySelectorAll('.btn-test-sd').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const dev = this.safetyDevices.find(d => d.id === id);
        if (dev) this.openAddTestModal(dev);
      });
    });

    this.querySelectorAll('.btn-edit-sd').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const dev = this.safetyDevices.find(d => d.id === id);
        if (dev) this.openEditModal(dev);
      });
    });

    this.querySelectorAll('.btn-inactivate-sd').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const tag = (e.currentTarget as HTMLElement).getAttribute('data-tag');
        if (id && tag) this.openInactivateModal(id, tag);
      });
    });

    this.querySelectorAll('.btn-reactivate-sd').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const tag = (e.currentTarget as HTMLElement).getAttribute('data-tag');
        if (id && confirm(`Deseja reativar o dispositivo ${tag}?`)) {
          try {
            await window.app.platform.safetyDeviceService.reactivateSafetyDevice(this.ctxForDevice(id), id, "Reativação de dispositivo de segurança");
            void this.loadData();
          } catch (err) {
            alert(`Erro ao reativar: ${this.errorText(err)}`);
          }
        }
      });
    });

    this.querySelectorAll('.btn-delete-sd').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const tag = (e.currentTarget as HTMLElement).getAttribute('data-tag');
        if (id && tag) this.openDeleteModal(id, tag);
      });
    });
  }

  // CREATE SAFETY DEVICE MODAL
  private openCreateModal() {
    const modalContainer = this.querySelector('#sd-modal-container') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 680px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="plus" style="color: var(--status-danger-solid); width: 20px; height: 20px;"></i> Cadastrar Dispositivo de Segurança (PSV)
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-create-sd" style="padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px;">
            <div id="sd-cascade-host"></div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">TAG da PSV / Dispositivo *</label>
                <input type="text" name="tag" required placeholder="Ex: PSV-101A, PSV-201, RD-301" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tipo de Dispositivo *</label>
                <select name="deviceType" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <option value="PSV">Válvula de Segurança e Alívio (PSV)</option>
                  <option value="PRV">Válvula Reguladora de Pressão (PRV)</option>
                  <option value="RELIEF_VALVE">Válvula de Alívio</option>
                  <option value="SAFETY_VALVE">Válvula de Segurança</option>
                  <option value="RUPTURE_DISC">Disco de Ruptura (RD)</option>
                  <option value="VACUUM_RELIEF_VALVE">Válvula Quebra-Vácuo</option>
                  <option value="PRESSURE_VACUUM_VALVE">Válvula Pressão/Vácuo (PVRV)</option>
                  <option value="BREATHER_VALVE">Válvula de Respiro</option>
                  <option value="OTHER">Outro</option>
                </select>
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nome / Descrição Resumida *</label>
              <input type="text" name="name" required placeholder="Ex: Válvula de Segurança do Vaso V-101" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
            </div>


            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Pressão de Ajuste (Set Pressure) *</label>
                <input type="number" step="any" name="setPressure" required placeholder="Ex: 10.5" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-weight: 600;">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Unidade de Pressão *</label>
                <input type="text" name="setPressureUnit" required value="kgf/cm²" placeholder="kgf/cm², bar, psi" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Orifício (API 526)</label>
                <input type="text" name="orificeDesignation" placeholder="Ex: D, E, F, G, H, J, K..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tamanho Entrada</label>
                <input type="text" name="inletSize" placeholder="Ex: 2'' 300# RF" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tamanho Saída</label>
                <input type="text" name="outletSize" placeholder="Ex: 3'' 150# RF" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Contrapressão</label>
                <input type="number" step="any" name="backPressure" placeholder="0" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Fabricante</label>
                <input type="text" name="manufacturer" placeholder="Ex: Leser, Crosby, Anderson" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Modelo</label>
                <input type="text" name="model" placeholder="Ex: 441, JOS-E" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nº de Série</label>
                <input type="text" name="serialNumber" placeholder="Ex: NS-78219" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Material do Corpo</label>
                <input type="text" name="bodyMaterial" placeholder="Ex: A216 WCB, A351 CF8M" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Material dos Internos / Trim</label>
                <input type="text" name="trimMaterial" placeholder="Ex: Inox 316, Stellite" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Observações Gerais</label>
              <textarea name="notes" rows="2" placeholder="Notas técnicas e dados adicionais..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-sd">Cancelar</button>
              <button type="submit" class="btn-primary-sd">Salvar Dispositivo</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Plus },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    // CLIENTE * → PROJETO → UNIDADE / ÁREA → EQUIPAMENTO PROTEGIDO, começando
    // pelo recorte que a listagem já tem selecionado.
    const cascade = new AssetCascadeFields(
      modalContainer.querySelector('#sd-cascade-host') as HTMLElement,
      this.cascadeSources(),
      { ...this.filter },
      { lockClient: false, equipmentLabel: "Equipamento Protegido" }
    );
    void cascade.mount();

    const form = modalContainer.querySelector('#form-create-sd') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      // O Cliente vem do formulário — nunca do cabeçalho nem da sessão.
      const links = cascade.value;
      if (!links.clientId) {
        alert('Selecione o Cliente do Dispositivo de Segurança.');
        return;
      }

      const newDev: Partial<SafetyDevice> = {
        tag: (formData.get('tag') as string).trim().toUpperCase(),
        name: formData.get('name') as string,
        deviceType: formData.get('deviceType') as SafetyDeviceType,
        projectId: links.projectId || null,
        protectedEquipmentId: links.equipmentId ? links.equipmentId as EquipmentId : null,
        areaId: links.areaId ? links.areaId as AreaId : null,
        setPressure: formData.get('setPressure') ? Number(formData.get('setPressure')) : null,
        setPressureUnit: (formData.get('setPressureUnit') as string) || 'kgf/cm²',
        orificeDesignation: (formData.get('orificeDesignation') as string) || null,
        inletSize: (formData.get('inletSize') as string) || null,
        outletSize: (formData.get('outletSize') as string) || null,
        backPressure: formData.get('backPressure') ? Number(formData.get('backPressure')) : null,
        manufacturer: (formData.get('manufacturer') as string) || null,
        model: (formData.get('model') as string) || null,
        serialNumber: (formData.get('serialNumber') as string) || null,
        bodyMaterial: (formData.get('bodyMaterial') as string) || null,
        trimMaterial: (formData.get('trimMaterial') as string) || null,
        notes: (formData.get('notes') as string) || null,
        status: 'ACTIVE'
      };

      try {
        await window.app.platform.safetyDeviceService.createSafetyDevice(this.ctxFor(links.clientId), newDev);
        closeModal();
        void this.loadData();
      } catch (err) {
        alert(`Erro ao criar dispositivo de segurança: ${this.errorText(err)}`);
      }
    });
  }

  // EDIT SAFETY DEVICE MODAL
  private openEditModal(dev: SafetyDevice) {
    const modalContainer = this.querySelector('#sd-modal-container') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 680px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="edit-3" style="color: var(--status-danger-solid); width: 20px; height: 20px;"></i> Editar Dispositivo (${dev.tag})
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-edit-sd" style="padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px;">
            <div id="sd-cascade-host"></div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">TAG da PSV / Dispositivo *</label>
                <input type="text" name="tag" required value="${dev.tag}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tipo de Dispositivo *</label>
                <select name="deviceType" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <option value="PSV" ${dev.deviceType === 'PSV' ? 'selected' : ''}>Válvula de Segurança e Alívio (PSV)</option>
                  <option value="PRV" ${dev.deviceType === 'PRV' ? 'selected' : ''}>Válvula Reguladora de Pressão (PRV)</option>
                  <option value="RELIEF_VALVE" ${dev.deviceType === 'RELIEF_VALVE' ? 'selected' : ''}>Válvula de Alívio</option>
                  <option value="SAFETY_VALVE" ${dev.deviceType === 'SAFETY_VALVE' ? 'selected' : ''}>Válvula de Segurança</option>
                  <option value="RUPTURE_DISC" ${dev.deviceType === 'RUPTURE_DISC' ? 'selected' : ''}>Disco de Ruptura (RD)</option>
                  <option value="VACUUM_RELIEF_VALVE" ${dev.deviceType === 'VACUUM_RELIEF_VALVE' ? 'selected' : ''}>Válvula Quebra-Vácuo</option>
                  <option value="PRESSURE_VACUUM_VALVE" ${dev.deviceType === 'PRESSURE_VACUUM_VALVE' ? 'selected' : ''}>Válvula Pressão/Vácuo (PVRV)</option>
                  <option value="BREATHER_VALVE" ${dev.deviceType === 'BREATHER_VALVE' ? 'selected' : ''}>Válvula de Respiro</option>
                  <option value="OTHER" ${dev.deviceType === 'OTHER' ? 'selected' : ''}>Outro</option>
                </select>
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nome / Descrição Resumida *</label>
              <input type="text" name="name" required value="${dev.name}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Pressão de Ajuste (Set) *</label>
                <input type="number" step="any" name="setPressure" required value="${dev.setPressure ?? ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-weight: 600;">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Unidade de Pressão *</label>
                <input type="text" name="setPressureUnit" required value="${dev.setPressureUnit || 'kgf/cm²'}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Orifício (API 526)</label>
                <input type="text" name="orificeDesignation" value="${dev.orificeDesignation || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tamanho Entrada</label>
                <input type="text" name="inletSize" value="${dev.inletSize || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tamanho Saída</label>
                <input type="text" name="outletSize" value="${dev.outletSize || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Status</label>
                <select name="status" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <option value="ACTIVE" ${dev.status === 'ACTIVE' ? 'selected' : ''}>Ativo</option>
                  <option value="OUT_OF_SERVICE" ${dev.status === 'OUT_OF_SERVICE' ? 'selected' : ''}>Fora de Serviço</option>
                  <option value="INACTIVE" ${dev.status === 'INACTIVE' ? 'selected' : ''}>Inativo</option>
                </select>
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Fabricante</label>
                <input type="text" name="manufacturer" value="${dev.manufacturer || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Modelo</label>
                <input type="text" name="model" value="${dev.model || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nº de Série</label>
                <input type="text" name="serialNumber" value="${dev.serialNumber || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Observações</label>
              <textarea name="notes" rows="2" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;">${dev.notes || ''}</textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-sd">Cancelar</button>
              <button type="submit" class="btn-primary-sd">Salvar Alterações</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Edit3 },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    // Edição: o Cliente PERSISTIDO do dispositivo é carregado e fica fixo —
    // nunca o Cliente da sessão nem o do filtro.
    const cascade = new AssetCascadeFields(
      modalContainer.querySelector('#sd-cascade-host') as HTMLElement,
      this.cascadeSources(),
      {
        clientId: String(dev.clientId),
        projectId: dev.projectId ?? "",
        areaId: dev.areaId ?? "",
        equipmentId: dev.protectedEquipmentId ?? ""
      },
      { lockClient: true, equipmentLabel: "Equipamento Protegido" }
    );
    void cascade.mount();

    const form = modalContainer.querySelector('#form-edit-sd') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const links = cascade.value;

      const updated: Partial<SafetyDevice> & { id: string } = {
        id: dev.id,
        tag: (formData.get('tag') as string).trim().toUpperCase(),
        name: formData.get('name') as string,
        deviceType: formData.get('deviceType') as SafetyDeviceType,
        projectId: links.projectId || null,
        protectedEquipmentId: links.equipmentId ? links.equipmentId as EquipmentId : null,
        areaId: links.areaId ? links.areaId as AreaId : null,
        setPressure: formData.get('setPressure') ? Number(formData.get('setPressure')) : null,
        setPressureUnit: (formData.get('setPressureUnit') as string) || 'kgf/cm²',
        orificeDesignation: (formData.get('orificeDesignation') as string) || null,
        inletSize: (formData.get('inletSize') as string) || null,
        outletSize: (formData.get('outletSize') as string) || null,
        status: (formData.get('status') as SafetyDeviceStatus) || 'ACTIVE',
        manufacturer: (formData.get('manufacturer') as string) || null,
        model: (formData.get('model') as string) || null,
        serialNumber: (formData.get('serialNumber') as string) || null,
        notes: (formData.get('notes') as string) || null
      };

      try {
        await window.app.platform.safetyDeviceService.updateSafetyDevice(this.ctxFor(String(dev.clientId)), updated);
        closeModal();
        void this.loadData();
      } catch (err) {
        alert(`Erro ao atualizar dispositivo: ${this.errorText(err)}`);
      }
    });
  }

  // ADD BENCH TEST MODAL (PSV TEST BENCH)
  private openAddTestModal(dev: SafetyDevice) {
    const modalContainer = this.querySelector('#sd-modal-container') as HTMLElement;
    if (!modalContainer) return;

    const todayStr = todayLocalDay();
    const nextYearDate = new Date();
    nextYearDate.setFullYear(nextYearDate.getFullYear() + 1);
    const nextYearStr = localDayOf(nextYearDate);

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 620px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-danger-solid); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="calendar" style="width: 20px; height: 20px;"></i> Registrar Teste de Bancada (${dev.tag})
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-add-test" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Data do Teste *</label>
                <input type="date" name="date" required value="${todayStr}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nº do Certificado / Laudo *</label>
                <input type="text" name="certificate" required placeholder="Ex: LAUDO-PSV-2026-104" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Resultado do Teste *</label>
                <select name="result" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <option value="CONFORMING">Aprovada</option>
                  <option value="ADJUSTED">Ajustada na Bancada (Aprovada)</option>
                  <option value="NON_CONFORMING">Reprovada</option>
                </select>
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Próximo Teste (Vencimento) *</label>
                <input type="date" name="nextDueDate" required value="${nextYearStr}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Pressão Encontrada (${dev.setPressureUnit || 'kgf/cm²'})</label>
                <input type="number" step="any" name="setPressureFound" placeholder="Ex: 10.8" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Pressão Ajustada Final (${dev.setPressureUnit || 'kgf/cm²'})</label>
                <input type="number" step="any" name="setPressureAdjusted" placeholder="Ex: 10.5" value="${dev.setPressure || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-weight: 600;">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Estanqueidade (API 527)</label>
                <input type="text" name="bubbleCount" placeholder="Ex: 0 bolhas/min (Conforme API 527)" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Bancada de Teste / Empresa</label>
                <input type="text" name="testBench" placeholder="Ex: Bancada CalibraFlow B-01" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Responsável Técnico / PLH</label>
              <input type="text" name="responsibleName" placeholder="Ex: Eng. Marcelo Costa (CREA 654321)" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Observações do Teste</label>
              <textarea name="notes" rows="2" placeholder="Observações..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-sd">Cancelar</button>
              <button type="submit" class="btn-primary-sd">Salvar Teste de Bancada</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Calendar },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-add-test') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);

      const testData: any = {
        safetyDeviceId: dev.id,
        date: formData.get('date') as string,
        certificate: formData.get('certificate') as string,
        result: formData.get('result') as any,
        nextDueDate: formData.get('nextDueDate') as string,
      };
      if (formData.get('setPressureFound')) testData.setPressureFound = Number(formData.get('setPressureFound'));
      if (formData.get('setPressureAdjusted')) testData.setPressureAdjusted = Number(formData.get('setPressureAdjusted'));
      if (formData.get('bubbleCount')) testData.bubbleCount = formData.get('bubbleCount') as string;
      if (formData.get('testBench')) testData.testBench = formData.get('testBench') as string;
      if (formData.get('responsibleName')) testData.responsibleName = formData.get('responsibleName') as string;
      if (formData.get('notes')) testData.notes = formData.get('notes') as string;

      try {
        await window.app.platform.safetyDeviceService.addTest(this.ctxFor(String(dev.clientId)), testData);
        closeModal();
        this.loadData();
      } catch (err: any) {
        alert(`Erro ao registrar teste: ${err.message}`);
      }
    });
  }

  // INACTIVATE MODAL
  private openInactivateModal(id: string, tag: string) {
    const modalContainer = this.querySelector('#sd-modal-container') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 480px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-warning-text); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="power" style="width: 20px; height: 20px;"></i> Inativar Dispositivo ${tag}
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-inactivate-sd" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <p style="margin: 0; font-size: 0.875rem; color: var(--color-text-primary);">
              A inativação preservará todo o histórico de calibrações e testes de bancada, alterando o status do dispositivo para <strong>INATIVO</strong>.
            </p>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Motivo / Justificativa da Inativação *</label>
              <textarea name="reason" required minlength="3" rows="3" placeholder="Ex: Válvula desativada ou descartada após laudo reprovado..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-sd">Cancelar</button>
              <button type="submit" style="background: var(--status-warning-text); color: var(--color-text-inverse); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Confirmar Inativação</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Power },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-inactivate-sd') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const reason = (form.querySelector('textarea[name="reason"]') as HTMLTextAreaElement).value;

      try {
        await window.app.platform.safetyDeviceService.inactivateSafetyDevice(this.ctxForDevice(id), id, reason);
        closeModal();
        this.loadData();
      } catch (err: any) {
        alert(`Erro ao inativar dispositivo: ${err.message}`);
      }
    });
  }

  // DELETE MODAL (CONTROLLED HARD DELETE)
  private openDeleteModal(id: string, tag: string) {
    const modalContainer = this.querySelector('#sd-modal-container') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 500px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-danger-solid); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="trash-2" style="width: 20px; height: 20px;"></i> Exclusão de Dispositivo de Segurança
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-delete-sd" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <div style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 6px; padding: 12px; color: var(--status-danger-border); font-size: 0.85rem;">
              <strong>Atenção:</strong> A exclusão definitiva será rejeitada se este dispositivo possuir histórico de testes de bancada ou documentos vinculados.
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Motivo / Justificativa da Exclusão de ${tag} *</label>
              <textarea name="reason" required minlength="3" rows="3" placeholder="Ex: Cadastro duplicado..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-sd">Cancelar</button>
              <button type="submit" style="background: var(--status-danger-solid); color: var(--color-text-primary); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Excluir Dispositivo</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Trash2 },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-delete-sd') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const reason = (form.querySelector('textarea[name="reason"]') as HTMLTextAreaElement).value;

      try {
        const res = await window.app.platform.safetyDeviceService.deleteSafetyDevice(this.ctxForDevice(id), id, reason);
        alert(res.message);
        closeModal();
        this.loadData();
      } catch (err: any) {
        alert(err.safeMessage || err.message);
      }
    });
  }
}

if (!customElements.get("app-safety-devices-page")) {
  customElements.define("app-safety-devices-page", AppSafetyDevicesPage);
}



